/**
 * Standard role & permission helpers — use anywhere in the app via useRolePermissions().
 * Role access objects are set on admin login and stored in Redux + LoginSession.
 */

import {
  getPermissionPresetForPosition,
  moduleHasAnyPermission,
  MODULE_ACTION_KEYS,
  normalizeModulePermissions,
} from '../data/permissionsConfig';
import { PLATFORM_ROLE } from '../data/teamConfig';
import { getRequiredModuleForAdminPath, isAdminAddPath } from './adminRoutePermissions';

/** Sidebar item label → required module (view). null = always visible. undefined = full-access only. */
export const SIDEBAR_MODULE_REQUIREMENTS = {
  Dashboard: null,
  Accounts: null, // parent; visibility driven by Customers / Businesses children
  Customers: 'customers',
  Businesses: 'businesses',
  Activity: null, // parent; visibility driven by Posts / Reviews / Messages / Quotes children
  Posts: 'posts_management',
  Reviews: 'reviews_management',
  Messages: 'messages',
  Quotes: 'quotes',
  Cases: null, // parent; visibility driven by case-module children
  Moderation: 'moderation',
  Compliance: 'compliance',
  'Fraud & Security': 'fraud_security',
  Support: 'support_tickets',
  'Support Tickets': 'support_tickets',
  Billing: null, // parent; visibility driven by Billing & Subscriptions child
  'Billing & Subscriptions': 'billing_subscriptions',
  'Pricing Plans': 'pricing_plans',
  Insights: null, // parent; visibility driven by Analytics / Reports children
  Analytics: 'analytics',
  Reports: 'reports',
  Content: 'content',
  Registers: null, // parent; visibility driven by register children
  'Trade Categories': 'trade_categories_management',
  Keyword: 'keywords_management',
  Keywords: 'keywords_management',
  'Form Inventory': 'form_inventory',
  Appeals: 'appeals',
  'Licences, Badges & Tickets': 'compliance_items_management',
  Admin: null, // parent; visibility driven by admin children
  'Team Members': 'team_members',
  Settings: 'settings',
  'System Logs': 'system_logs',
  Configuration: 'configuration',
  Integrations: 'integrations',
  'Change History': 'change_history',
  'Log Out': null,
};

/** Dashboard widget id → module required for view access. null = visible to any role with dashboard access. */
export const DASHBOARD_WIDGET_MODULE_MAP = {
  'review-activity': 'reviews_management',
  'post-activity': 'posts_management',
  'customer-activity': 'customers',
  'live-activity': null,
  'business-activity': 'businesses',
  'api-health': 'analytics',
  'message-activity': 'messages',
  'quote-activity': 'quotes',
  'moderation-activity': 'moderation',
  'platform-health': 'analytics',
  'compliance-activity': 'compliance',
  'fraud-security-activity': 'fraud_security',
  'billing-subscriptions': 'billing_subscriptions',
  'support-activity': 'support_tickets',
  'australia-activity-map': 'analytics',
};

/** Widgets that only make sense with full analytics — hidden when analytics is related-records only. */
const RELATED_ANALYTICS_EXCLUDED_WIDGETS = new Set([
  'api-health',
  'platform-health',
  'australia-activity-map',
]);

function getModuleScope(activeRole, moduleKey) {
  if (!moduleKey || hasFullAccess(activeRole)) return null;
  const map = getModulePermissionMap(activeRole?.modulePermissions);
  return map.get(moduleKey)?.dataAccessScope || null;
}

/**
 * Demo team roles returned on admin login until API provides availableRoles.
 * Permissions are resolved from permissionsConfig presets (same as Permissions & Access forms).
 */
export const DEMO_TEAM_ROLE_ACCOUNTS = [
  {
    accountId: 'admin',
    firstName: 'Olivia',
    lastName: 'Grant',
    team: 'Administration Team',
    position: 'Administrator',
    sidebarLabel: 'Admin',
    platformRole: PLATFORM_ROLE.TEAM_MEMBER,
    memberSince: '2024-03-12T00:00:00.000Z',
  },
  {
    accountId: 'operations',
    firstName: 'Jordan',
    lastName: 'Blake',
    team: 'Operations Team',
    position: 'Operations Lead',
    sidebarLabel: 'Operations Team',
    platformRole: PLATFORM_ROLE.TEAM_MEMBER,
    memberSince: '2024-06-18T00:00:00.000Z',
  },
  {
    accountId: 'support',
    firstName: 'Michael',
    lastName: 'Chen',
    team: 'Support Team',
    position: 'Support Lead',
    sidebarLabel: 'Support Team',
    platformRole: PLATFORM_ROLE.TEAM_MEMBER,
    memberSince: '2025-01-09T00:00:00.000Z',
  },
  {
    accountId: 'support-manager',
    firstName: 'Sara',
    lastName: 'Mills',
    team: 'Support Team',
    position: 'Support Manager',
    sidebarLabel: 'Support Manager',
    platformRole: PLATFORM_ROLE.TEAM_MEMBER,
    memberSince: '2024-11-04T00:00:00.000Z',
  },
  {
    accountId: 'finance',
    firstName: 'Emma',
    lastName: 'Taylor',
    team: 'Finance Team',
    position: 'Accountant',
    sidebarLabel: 'Finance Team',
    platformRole: PLATFORM_ROLE.TEAM_MEMBER,
    memberSince: '2025-02-20T00:00:00.000Z',
  },
  {
    accountId: 'development',
    firstName: 'Ali',
    lastName: 'Khan',
    team: 'Development Team',
    position: 'Full-stack Developer',
    sidebarLabel: 'Development Team',
    platformRole: PLATFORM_ROLE.TEAM_MEMBER,
    memberSince: '2025-05-14T00:00:00.000Z',
  },
];

export const FULL_ACCESS_ACCOUNT_ID = 'owner';

export function buildRoleAccessFromAccount(account = {}) {
  const position = account.position || '';
  const preset = getPermissionPresetForPosition(position);
  const modulePermissions = normalizeModulePermissions(
    account.modulePermissions?.length
      ? account.modulePermissions
      : preset?.modulePermissions || [],
  );
  const firstName = account.firstName || '';
  const lastName = account.lastName || '';
  const name = account.name || `${firstName} ${lastName}`.trim();

  return {
    accountId: account.accountId || account.id || '',
    platformRole: account.platformRole || PLATFORM_ROLE.TEAM_MEMBER,
    team: account.team || '',
    position,
    roleLabel: account.roleLabel || position || account.platformRole || '',
    sidebarLabel:
      account.sidebarLabel ||
      account.roleLabel ||
      account.team ||
      position ||
      account.platformRole ||
      '',
    name,
    firstName,
    lastName,
    profilePicture: account.profilePicture || null,
    memberSince: account.memberSince || account.startDate || null,
    startDate: account.startDate || account.memberSince || null,
    dashboard: account.dashboard || preset?.dashboard || 'Team Dashboard',
    defaultAccessLabel:
      account.defaultAccessLabel || preset?.defaultAccessLabel || `${position} Access`,
    permissionPreset: account.permissionPreset || preset?.presetName || `${position} Preset`,
    modulePermissions,
    isFullAccess: Boolean(account.isFullAccess),
  };
}

export function buildFullAccessRoleFromUser(user = {}) {
  return buildRoleAccessFromAccount({
    accountId: FULL_ACCESS_ACCOUNT_ID,
    platformRole: user.platformRole || PLATFORM_ROLE.OWNER,
    team: user.department || user.team || 'Management',
    position: user.jobTitle || user.position || 'System Administrator',
    roleLabel: 'Owner',
    sidebarLabel: 'Owner',
    name: 'Saim Sahi',
    firstName: 'Saim',
    lastName: 'Sahi',
    profilePicture: user.profilePicture || null,
    memberSince: user.startDate || user.memberSince || '2024-01-01T00:00:00.000Z',
    startDate: user.startDate || user.memberSince || '2024-01-01T00:00:00.000Z',
    dashboard: 'Main dashboard',
    isFullAccess: true,
    modulePermissions: [],
  });
}

export function buildDemoTeamRoles() {
  return DEMO_TEAM_ROLE_ACCOUNTS.map((account) => buildRoleAccessFromAccount(account));
}

/**
 * Permissions & Access table rows built from temporary switch-account role presets.
 */
export function buildSwitchAccountAccessMembers() {
  return DEMO_TEAM_ROLE_ACCOUNTS.map((account, index) => {
    const role = buildRoleAccessFromAccount(account);
    return {
      id: `switch-${account.accountId}`,
      teamMemberId: `TM-${String(100 + index).padStart(4, '0')}`,
      name: role.name,
      firstName: role.firstName,
      lastName: role.lastName,
      workEmail: `${account.accountId}@tradeposter.com`,
      mobileNumber: '+61 400 000 000',
      approvedWorkCountry: 'Australia',
      team: role.team,
      position: role.position,
      jobTitle: role.position,
      platformRole: PLATFORM_ROLE.TEAM_MEMBER,
      permissionId: `PER-${String(index + 1).padStart(4, '0')}`,
      permissionPreset: role.permissionPreset,
      defaultAccessLabel: role.defaultAccessLabel,
      permissionsAccessLabel: role.defaultAccessLabel,
      permissionName: role.defaultAccessLabel,
      dashboard: role.dashboard,
      hasCustomisedAccess: false,
      customPermission: null,
      permissionsAdded: [],
      permissionsRemoved: [],
      modulePermissions: role.modulePermissions,
      status: 'Active',
      memberSince: role.memberSince || role.startDate,
      lastUpdated: role.memberSince || role.startDate,
      lastUpdatedBy: 'Saim Sahi',
      isOwner: false,
      isCoOwner: false,
      requirePasswordChange: false,
      switchAccountId: account.accountId,
      sidebarLabel: role.sidebarLabel,
    };
  });
}

/** Build admin session payload stored on login (LoginSession + Redux). */
export function buildAdminSessionFromUser(user = {}, options = {}) {
  const availableRoles = options.availableRoles || buildDemoTeamRoles();
  const activeRole = options.activeRole || buildFullAccessRoleFromUser(user);
  return { activeRole, availableRoles };
}

/** Read admin session from a login payload (API / localStorage). */
export function getAdminSessionFromLogin(login = {}) {
  const user = login.user || login;
  const isSuperAdmin = login.roleType === 'superadmin' || user.roleType === 'superadmin';
  const activeRole = login.adminRole || user.adminRole || null;
  let availableRoles = login.adminAvailableRoles || user.adminAvailableRoles || [];

  // Temporary dev switch accounts — always use configured demo roles for superadmin.
  if (isSuperAdmin) {
    availableRoles = buildDemoTeamRoles();
  }

  if (activeRole) {
    const nextActive =
      !activeRole.isFullAccess && activeRole.accountId
        ? findRoleByAccountId(availableRoles, activeRole.accountId) || normalizeRoleAccess(activeRole)
        : normalizeRoleAccess(activeRole);
    return {
      activeRole: nextActive,
      availableRoles: (availableRoles || []).map(normalizeRoleAccess),
    };
  }

  if (isSuperAdmin) {
    return buildAdminSessionFromUser(user);
  }

  return { activeRole: null, availableRoles: [] };
}

export function normalizeRoleAccess(role = {}) {
  if (!role || typeof role !== 'object') return null;
  if (role.isFullAccess) {
    return buildRoleAccessFromAccount({ ...role, isFullAccess: true, modulePermissions: [] });
  }
  return buildRoleAccessFromAccount(role);
}

export function findRoleByAccountId(roles = [], accountId) {
  if (!accountId) return null;
  return (roles || []).find((role) => role.accountId === accountId) || null;
}

export function getModulePermissionMap(modulePermissions) {
  const map = new Map();
  normalizeModulePermissions(modulePermissions).forEach((row) => {
    if (row.module) map.set(row.module, row);
  });
  return map;
}

export function hasFullAccess(activeRole) {
  return Boolean(activeRole?.isFullAccess);
}

export function hasModuleViewAccess(activeRole, moduleKey) {
  if (!moduleKey) return true;
  if (hasFullAccess(activeRole)) return true;
  const map = getModulePermissionMap(activeRole?.modulePermissions);
  const row = map.get(moduleKey);
  return Boolean(row?.view) && moduleHasAnyPermission(row);
}

export function hasModuleActionAccess(activeRole, moduleKey, actionKey = 'view') {
  if (!moduleKey) return true;
  if (hasFullAccess(activeRole)) return true;
  const map = getModulePermissionMap(activeRole?.modulePermissions);
  const row = map.get(moduleKey);
  if (!row || !moduleHasAnyPermission(row)) return false;
  if (actionKey === 'view') return Boolean(row.view);
  return Boolean(row[actionKey]);
}

/**
 * Per-module action flags for the active role.
 * isViewOnly = can view but cannot add or edit.
 */
export function getModuleAccessFlags(activeRole, moduleKey) {
  if (!moduleKey) {
    return { canView: true, canAdd: true, canEdit: true, isViewOnly: false };
  }
  if (hasFullAccess(activeRole)) {
    return { canView: true, canAdd: true, canEdit: true, isViewOnly: false };
  }
  const canView = hasModuleActionAccess(activeRole, moduleKey, 'view');
  const canAdd = hasModuleActionAccess(activeRole, moduleKey, 'add');
  const canEdit = hasModuleActionAccess(activeRole, moduleKey, 'edit');
  return {
    canView,
    canAdd,
    canEdit,
    isViewOnly: canView && !canAdd && !canEdit,
  };
}

export function getPathModuleAccessFlags(pathname, activeRole) {
  const moduleKey = getRequiredModuleForAdminPath(pathname);
  if (moduleKey === null) {
    return { moduleKey: null, canView: true, canAdd: true, canEdit: true, isViewOnly: false };
  }
  if (moduleKey === undefined) {
    const full = hasFullAccess(activeRole);
    return {
      moduleKey: undefined,
      canView: full,
      canAdd: full,
      canEdit: full,
      isViewOnly: false,
    };
  }
  return { moduleKey, ...getModuleAccessFlags(activeRole, moduleKey) };
}

export function canAccessAdminPath(pathname, activeRole) {
  if (hasFullAccess(activeRole)) return true;
  const requiredModule = getRequiredModuleForAdminPath(pathname);
  if (requiredModule === null) return true;
  if (requiredModule === undefined) return false;
  if (!hasModuleViewAccess(activeRole, requiredModule)) return false;
  // View-only (and any role without Add) cannot open Add pages
  if (isAdminAddPath(pathname) && !hasModuleActionAccess(activeRole, requiredModule, 'add')) {
    return false;
  }
  return true;
}

export function createAccessContext(activeRole) {
  return {
    activeRole: activeRole || null,
    modulePermissions: activeRole?.modulePermissions || [],
    isFullAccess: hasFullAccess(activeRole),
  };
}

export function canAccessSidebarItem(linkName, accessContext = {}, parentLinkName = null) {
  if (linkName === 'Log Out' || linkName === 'Dashboard') return true;
  if (accessContext.isFullAccess) return true;

  let requiredModule = SIDEBAR_MODULE_REQUIREMENTS[linkName];
  if (requiredModule === undefined && parentLinkName) {
    requiredModule = SIDEBAR_MODULE_REQUIREMENTS[parentLinkName];
  }
  if (requiredModule === undefined) return false;
  if (requiredModule === null) return true;
  return hasModuleViewAccess(accessContext.activeRole, requiredModule);
}

export function filterSidebarLinks(links, accessContext) {
  return (links || [])
    .map((item) => {
      if (!canAccessSidebarItem(item.linkName, accessContext)) return null;

      if (item.subMenu?.length) {
        const subMenu = item.subMenu
          .map((sub) => {
            if (!canAccessSidebarItem(sub.linkName, accessContext, item.linkName)) return null;
            if (sub.subMenu?.length) {
              const nestedSubMenu = sub.subMenu.filter((subSub) =>
                canAccessSidebarItem(subSub.linkName, accessContext, sub.linkName),
              );
              if (!nestedSubMenu.length) return null;
              return { ...sub, subMenu: nestedSubMenu };
            }
            return sub;
          })
          .filter(Boolean);
        if (!subMenu.length && item.linkName !== 'Configuration') return null;
        return { ...item, subMenu };
      }

      return item;
    })
    .filter(Boolean);
}

export function filterDashboardWidgets(widgets, accessContext) {
  if (accessContext?.isFullAccess) return widgets || [];

  const activeRole = accessContext?.activeRole;
  const analyticsScope = getModuleScope(activeRole, 'analytics');
  const relatedAnalyticsOnly = analyticsScope === 'related_records';

  return (widgets || []).filter((widget) => {
    const moduleKey = DASHBOARD_WIDGET_MODULE_MAP[widget.id];
    if (moduleKey === null) return true;
    if (!moduleKey) return false;
    if (
      relatedAnalyticsOnly &&
      RELATED_ANALYTICS_EXCLUDED_WIDGETS.has(widget.id)
    ) {
      return false;
    }
    return hasModuleViewAccess(activeRole, moduleKey);
  });
}

export function attachAdminSessionToLoginPayload(loginPayload = {}, session = {}) {
  const activeRole = session.activeRole || null;
  const availableRoles = session.availableRoles || [];
  return {
    ...loginPayload,
    adminRole: activeRole,
    adminAvailableRoles: availableRoles,
    user: loginPayload.user
      ? {
          ...loginPayload.user,
          adminRole: activeRole,
          adminAvailableRoles: availableRoles,
        }
      : loginPayload.user,
  };
}

export function resolveSwitchedRole(accountId, availableRoles = [], fallbackUser = {}) {
  if (!accountId || accountId === FULL_ACCESS_ACCOUNT_ID) {
    return buildFullAccessRoleFromUser(fallbackUser);
  }
  const matched = findRoleByAccountId(availableRoles, accountId);
  return matched || buildFullAccessRoleFromUser(fallbackUser);
}

export { MODULE_ACTION_KEYS };
